import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Divider,
  FormControlLabel,
  IconButton,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import useDraftState from '@/hooks/useDraftState';
import useStyles from '@/pages/Administration/AdministrationGeneral/AdministrationGeneral.styles';

import type { PreferencesConfigurationProps } from './PreferencesConfiguration.types';

const PreferencesConfiguration = (props: PreferencesConfigurationProps) => {
  const { classes } = useStyles();
  const { handleSave, orgInfo } = props;
  const [editing, isEditing] = useState<boolean>(false);
  const [updatedOrgInfo, updateDraft, resetDraft] = useDraftState(orgInfo);

  const handleTimeoutChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDraft({ session_timeout: Number(event.target.value || 0) });
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDraft({ enable_ticket_assignee: event.target.checked });
  };

  return (
    <LargeCard
      title="Preferences"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton
                onClick={() => {
                  isEditing(!editing);
                  handleSave(updatedOrgInfo);
                }}
              >
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                onClick={() => {
                  isEditing(!editing);
                  resetDraft();
                }}
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => isEditing(!editing)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      {/* Session Timeout Section */}
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Session Timeout
      </Typography>
      <Typography className={classes.subText} variant="body2">
        Set the duration of user inactivity, after which they will be
        automatically logged out. This setting applies to all users in the
        organization.
      </Typography>
      {editing && (
        <Alert severity="warning" sx={{ my: 1 }}>
          Please consult your risk, compliance, or audit teams before extending
          the session time-out setting. Changes may have PCI or other regulatory
          implications.
        </Alert>
      )}
      <InputLabel sx={{ mt: '1rem' }} id="sessionTimeoutLabel">
        Number of minutes
      </InputLabel>
      <TextField
        className={classes.textField}
        onChange={handleTimeoutChange}
        aria-labelledby="sessionTimeout"
        name="sessionTimeout"
        variant="outlined"
        disabled={!editing}
        type="number"
        value={updatedOrgInfo.session_timeout}
      />

      <Divider sx={{ my: 1.5 }} />

      {/* Ticket Assignment Section */}
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Ticket Assignment
      </Typography>
      <Typography className={classes.subText} variant="body2">
        Allow users to assign tickets to other users in the organization.
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={updatedOrgInfo.enable_ticket_assignee || false}
            onChange={handleToggleChange}
            disabled={!editing}
            color="primary"
            name="enableTicketAssignment"
          />
        }
        label="Enable ticket assignment"
      />
    </LargeCard>
  );
};

export default PreferencesConfiguration;
